import React, { useEffect, useState } from "react";
import { CompanyReservationCode, getMatch, hasMatchingCodeImplementation, hasMatchingReservationImplementation } from "../../../../model/CompanyReservationCode";
import { MenuItem, Tab, Tabs, TextField } from "@material-ui/core";
import TabPanel from "../../../Common/TabPanel";
import useDidMountEffect from "../../../Common/UseDidMountEffect";

type Props = {
    selectableCodes: CompanyReservationCode[];
    productId: string;
    variantId: string;
    initialValue?: string;
    onChange: (codeHolderId: string) => void;
};

type DefaultValues = {
    initialValue?: string;
    selectableCodes: CompanyReservationCode[];
    activeTab: 0 | 1;
}

const getDefaults = (props: Props): DefaultValues => {
    const { selectableCodes, productId, variantId, initialValue } = props;

    let activeTab: 0 | 1 = 0;

    if (!!productId && !!variantId && !!initialValue) {
        const match = getMatch(selectableCodes, productId, variantId, initialValue);
        if (match) {
            activeTab = match.reservationName ? 0 : 1;
        }
    }

    return {
        initialValue: initialValue ?? "",
        selectableCodes,
        activeTab
    };
};

const ReservationCodeSelector = (props: Props) => {
    const defaults = getDefaults(props);

    const [codes, setCodes] = useState<CompanyReservationCode[]>([]);
    const [reservations, setReservations] = useState<CompanyReservationCode[]>([]);
    const [codeHolderId, setCodeHolderId] = useState<string>(defaults.initialValue);
    const [activeTab, setActiveTab] = useState<number>(defaults.activeTab);

    useEffect(() => {
        const { productId, variantId, initialValue } = props;

        let nextCodes: CompanyReservationCode[] = [];
        let nextReservations: CompanyReservationCode[] = [];

        if (!!productId && !!variantId) {
            nextCodes = defaults.selectableCodes?.filter(x => hasMatchingCodeImplementation(x, productId, variantId));
            nextReservations = defaults.selectableCodes?.filter(x => hasMatchingReservationImplementation(x, productId, variantId));
        }

        if (!initialValue) {
            setCodeHolderId("");
        }

        setCodes(nextCodes);
        setReservations(nextReservations);
    }, [props.productId, props.variantId]);

    useDidMountEffect(() => {
        const { onChange } = props;
        onChange(codeHolderId);
    }, [codeHolderId]);

    const handleReservationIdChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCodeHolderId(e.target.value);
    };

    const renderFieldFor = (mode: "RESERVATION" | "CODE") => {
        const { variantId } = props;

        let items: CompanyReservationCode[];
        let label: string;
        let disabledValue: string;

        if (mode === "RESERVATION") {
            items = reservations;
            label = "Reservation";
            disabledValue = !!variantId ? "Inga reservationer för vald produkt/variant" : "";
        } else {
            items = codes;
            label = "Kod";
            disabledValue = !!variantId ? "Inga koder för vald produkt/variant" : "";
        }

        if (!items || items.length === 0) {
            return (
                <TextField
                    style={{ flex: 1, margin: "0 3px" }}
                    label={label}
                    InputLabelProps={{ shrink: true }}
                    value={disabledValue}
                    fullWidth
                    disabled={true}
                >
                </TextField>
            );
        }

        const safeValue = items.some(x => x.codeHolderId === codeHolderId) ? codeHolderId : "";

        return (
            <TextField
                select
                style={{ flex: 1, margin: "0 3px" }}
                label={label}
                InputLabelProps={{ shrink: true }}
                value={safeValue}
                fullWidth
                onChange={handleReservationIdChanged}
            >
                {items?.map((p, idx) => {
                    return <MenuItem key={idx} value={p.codeHolderId}>{p.reservationName ?? p.codeHolderName}</MenuItem>;
                })}
            </TextField>
        );
    };

    const handleTabChange = (event: React.ChangeEvent<{}>, nextValue: number) => {
        setActiveTab(nextValue);
    };

    const renderTabs = () => {
        const reservationDisabled = !reservations || reservations.length === 0;
        const codesDisabled = !codes || codes.length === 0;

        let activeTabValue = activeTab;
        if (codesDisabled) {
            activeTabValue = 0; // Always show Reservations if Codes are disabled
        } else if (reservationDisabled && !codesDisabled) {
            activeTabValue = 1; // Show Codes if not disabled, but Reservations are
        }

        return (<>
            <Tabs
                value={activeTabValue}
                onChange={handleTabChange}
            >
                <Tab label="Reservation" disabled={reservationDisabled} />
                <Tab label="Kod" disabled={codesDisabled} />
            </Tabs>
            <TabPanel value={activeTabValue} index={0}>
                {renderFieldFor("RESERVATION")}
            </TabPanel>
            <TabPanel value={activeTabValue} index={1}>
                {renderFieldFor("CODE")}
            </TabPanel>
        </>
        );
    };

    return renderTabs();
};

export default ReservationCodeSelector;